import React, { useState, useEffect } from 'react';
import { adminAPI } from '../../services/api';
import { User } from '../../types';
import { useAuth } from '../../context/AuthContext';
import './AdminUsers.css';

interface UserEditModal {
  user: User;
  isOpen: boolean;
}

interface ConfirmModal {
  isOpen: boolean;
  title: string;
  message: string;
  type: 'delete' | 'toggleAdmin' | 'resetPassword';
  userId?: number;
  userName?: string;
}

const AdminUsers: React.FC = () => {
  const { user: currentUser } = useAuth();
  const [users, setUsers] = useState<User[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const [editModal, setEditModal] = useState<UserEditModal>({ user: {} as User, isOpen: false });
  const [confirmModal, setConfirmModal] = useState<ConfirmModal>({ isOpen: false, title: '', message: '', type: 'delete' });
  const [tempPassword, setTempPassword] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [filterAdmin, setFilterAdmin] = useState<'all' | 'admin' | 'user'>('all');

  useEffect(() => {
    loadUsers();
  }, []);

  const loadUsers = async () => {
    try {
      setIsLoading(true);
      const response = await adminAPI.getUsers({ limit: 1000 });
      setUsers(response.data.data.users);
    } catch (err: any) {
      setError('Error al cargar usuarios');
    } finally {
      setIsLoading(false);
    }
  };

  const handleUpdateUser = async (userData: Partial<User>) => {
    try {
      const response = await adminAPI.updateUser(editModal.user.id, userData);
      setUsers(prev => prev.map(u => u.id === editModal.user.id ? response.data.data.user : u));
      setSuccessMessage('Usuario actualizado exitosamente');
      setEditModal({ user: {} as User, isOpen: false });
      setTimeout(() => setSuccessMessage(''), 3000);
    } catch (error: any) {
      setError(error.response?.data?.message || 'Error al actualizar usuario');
      setTimeout(() => setError(''), 3000);
    }
  };

  const handleDeleteUser = async (userId: number) => {
    try {
      await adminAPI.deleteUser(userId);
      setUsers(prev => prev.filter(u => u.id !== userId));
      setSuccessMessage('Usuario eliminado exitosamente');
      setConfirmModal({ isOpen: false, title: '', message: '', type: 'delete' });
      setTimeout(() => setSuccessMessage(''), 3000);
    } catch (error: any) {
      setError(error.response?.data?.message || 'Error al eliminar usuario');
      setTimeout(() => setError(''), 3000);
    }
  };

  const handleToggleAdmin = async (userId: number) => {
    try {
      const response = await adminAPI.toggleUserAdmin(userId);
      setUsers(prev => prev.map(u => u.id === userId ? response.data.data.user : u));
      setSuccessMessage(response.data.message || 'Estado de administrador actualizado');
      setConfirmModal({ isOpen: false, title: '', message: '', type: 'toggleAdmin' });
      setTimeout(() => setSuccessMessage(''), 3000);
    } catch (error: any) {
      setError(error.response?.data?.message || 'Error al cambiar estado de administrador');
      setTimeout(() => setError(''), 3000);
    }
  };

  const handleResetPassword = async (userId: number) => {
    try {
      const response = await adminAPI.resetUserPassword(userId);
      setTempPassword(response.data.data.tempPassword);
      setSuccessMessage('Contraseña temporal generada');
      setConfirmModal({ isOpen: false, title: '', message: '', type: 'resetPassword' });
      setTimeout(() => {
        setSuccessMessage('');
        setTempPassword('');
      }, 10000);
    } catch (error: any) {
      setError(error.response?.data?.message || 'Error al resetear contraseña');
      setTimeout(() => setError(''), 3000);
    }
  };

  const openDeleteConfirm = (user: User) => {
    setConfirmModal({
      isOpen: true,
      type: 'delete',
      title: 'Confirmar Eliminación',
      message: `¿Estás seguro de que quieres eliminar al usuario "${user.name}"? Esta acción eliminará todos sus datos y no se puede deshacer.`,
      userId: user.id,
      userName: user.name
    });
  };

  const openToggleAdminConfirm = (user: User) => {
    setConfirmModal({
      isOpen: true,
      type: 'toggleAdmin',
      title: `${user.is_admin ? 'Remover' : 'Promover'} Administrador`,
      message: `¿Estás seguro de que quieres ${user.is_admin ? 'remover a' : 'promover a'} "${user.name}" ${user.is_admin ? 'de' : 'como'} administrador?`,
      userId: user.id,
      userName: user.name
    });
  };

  const openResetPasswordConfirm = (user: User) => {
    setConfirmModal({
      isOpen: true,
      type: 'resetPassword',
      title: 'Resetear Contraseña',
      message: `¿Estás seguro de que quieres generar una nueva contraseña temporal para "${user.name}"? Se mostrará la nueva contraseña que deberás comunicar al usuario.`,
      userId: user.id,
      userName: user.name
    });
  };

  const filteredUsers = users.filter(user => {
    const matchesSearch = user.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         user.email.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesFilter = filterAdmin === 'all' ||
                         (filterAdmin === 'admin' && user.is_admin) ||
                         (filterAdmin === 'user' && !user.is_admin);
    return matchesSearch && matchesFilter;
  });

  const handleConfirmAction = () => {
    const { type, userId } = confirmModal;
    if (!userId) return;

    switch (type) {
      case 'delete':
        handleDeleteUser(userId);
        break;
      case 'toggleAdmin':
        handleToggleAdmin(userId);
        break;
      case 'resetPassword':
        handleResetPassword(userId);
        break;
    }
  };

  if (isLoading) {
    return <div className="admin-loading">Cargando usuarios...</div>;
  }

  return (
    <div className="admin-users">
      <div className="admin-users-header">
        <h1>Gestión de Usuarios</h1>
        <div className="users-stats">
          <span className="stat-item">
            <strong>Total:</strong> {users.length}
          </span>
          <span className="stat-item">
            <strong>Admins:</strong> {users.filter(u => u.is_admin).length}
          </span>
          <span className="stat-item plan-stat free">
            <strong>Free:</strong> {users.filter(u => u.subscription_plan === 'free').length}
          </span>
          <span className="stat-item plan-stat premium">
            <strong>Premium:</strong> {users.filter(u => u.subscription_plan === 'premium').length}
          </span>
          <span className="stat-item plan-stat unlimited">
            <strong>Unlimited:</strong> {users.filter(u => u.subscription_plan === 'unlimited').length}
          </span>
        </div>
      </div>

      {error && <div className="error-message">{error}</div>}
      {successMessage && <div className="success-message">{successMessage}</div>}
      {tempPassword && (
        <div className="temp-password-message">
          <strong>Contraseña temporal generada:</strong> <code>{tempPassword}</code>
          <br />
          <small>Comunica esta contraseña al usuario. Expira en 10 segundos.</small>
        </div>
      )}

      <div className="users-filters">
        <div className="search-box">
          <input
            type="text"
            placeholder="Buscar por nombre o email..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="search-input"
          />
        </div>
        <div className="filter-box">
          <select
            value={filterAdmin}
            onChange={(e) => setFilterAdmin(e.target.value as 'all' | 'admin' | 'user')}
            className="filter-select"
          >
            <option value="all">Todos los usuarios</option>
            <option value="admin">Solo administradores</option>
            <option value="user">Solo usuarios regulares</option>
          </select>
        </div>
      </div>

      <div className="users-table-container">
        <table className="users-table">
          <thead>
            <tr>
              <th>ID</th>
              <th>Nombre</th>
              <th>Email</th>
              <th>Plan</th>
              <th>Tipo</th>
              <th>Estado</th>
              <th>Fecha Registro</th>
              <th>Acciones</th>
            </tr>
          </thead>
          <tbody>
            {filteredUsers.map(user => (
              <tr key={user.id} className={user.id === currentUser?.id ? 'current-user' : ''}>
                <td>{user.id}</td>
                <td>
                  <div className="user-info">
                    <strong>{user.name}</strong>
                    {user.id === currentUser?.id && <span className="current-badge">TÚ</span>}
                  </div>
                </td>
                <td>{user.email}</td>
                <td>
                  <span className={`plan-badge ${user.subscription_plan || 'premium'}`}>
                    {user.subscription_plan === 'free' && '🆓 Free'}
                    {user.subscription_plan === 'premium' && '⭐ Premium'}
                    {user.subscription_plan === 'unlimited' && '💎 Unlimited'}
                    {!user.subscription_plan && '⭐ Premium'}
                  </span>
                </td>
                <td>
                  <span className={`user-type ${user.is_admin ? 'admin' : 'regular'}`}>
                    {user.is_admin ? '👑 Admin' : '👤 Usuario'}
                  </span>
                </td>
                <td>
                  <span className={`status ${user.email_verified ? 'verified' : 'unverified'}`}>
                    {user.email_verified ? '✅ Verificado' : '⏳ Pendiente'}
                  </span>
                </td>
                <td>
                  {(() => {
                    if (!user.created_at) return 'Sin fecha';
                    try {
                      const date = new Date(user.created_at);
                      if (isNaN(date.getTime())) return 'Fecha inválida';
                      return date.toLocaleDateString('es-ES', {
                        day: '2-digit',
                        month: '2-digit',
                        year: 'numeric'
                      });
                    } catch (error) {
                      return 'Error en fecha';
                    }
                  })()}
                </td>
                <td>
                  <div className="action-buttons">
                    <button
                      onClick={() => setEditModal({ user, isOpen: true })}
                      className="btn btn-edit"
                      title="Editar usuario"
                    >
                      ✏️
                    </button>
                    
                    {user.id !== currentUser?.id && (
                      <>
                        <button
                          onClick={() => openToggleAdminConfirm(user)}
                          className={`btn btn-admin ${user.is_admin ? 'demote' : 'promote'}`}
                          title={user.is_admin ? 'Remover admin' : 'Promover a admin'}
                        >
                          {user.is_admin ? '👤' : '👑'}
                        </button>
                        
                        <button
                          onClick={() => openResetPasswordConfirm(user)}
                          className="btn btn-password"
                          title="Resetear contraseña"
                        >
                          🔑
                        </button>
                        
                        <button
                          onClick={() => openDeleteConfirm(user)}
                          className="btn btn-delete"
                          title="Eliminar usuario"
                        >
                          🗑️
                        </button>
                      </>
                    )}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {filteredUsers.length === 0 && (
        <div className="no-users">
          No se encontraron usuarios que coincidan con los filtros.
        </div>
      )}

      {/* Modal de Edición */}
      {editModal.isOpen && (
        <div className="modal-overlay">
          <div className="modal">
            <div className="modal-header">
              <h3>Editar Usuario</h3>
              <button onClick={() => setEditModal({ user: {} as User, isOpen: false })} className="modal-close">
                ✕
              </button>
            </div>
            <div className="modal-body">
              <form onSubmit={(e) => {
                e.preventDefault();
                const formData = new FormData(e.target as HTMLFormElement);
                handleUpdateUser({
                  name: formData.get('name') as string,
                  email: formData.get('email') as string,
                  subscription_plan: formData.get('subscription_plan') as 'free' | 'premium' | 'unlimited',
                  email_verified: formData.get('email_verified') === 'on',
                  follow_up_notifications: formData.get('follow_up_notifications') === 'on',
                  follow_up_enabled: formData.get('follow_up_enabled') === 'on'
                });
              }}>
                <div className="form-group">
                  <label>Nombre:</label>
                  <input
                    type="text"
                    name="name"
                    defaultValue={editModal.user.name}
                    required
                  />
                </div>
                
                <div className="form-group">
                  <label>Email:</label>
                  <input
                    type="email"
                    name="email"
                    defaultValue={editModal.user.email}
                    required
                  />
                </div>

                <div className="form-group">
                  <label>Plan de Suscripción:</label>
                  <select
                    name="subscription_plan"
                    defaultValue={editModal.user.subscription_plan || 'premium'}
                    className="plan-select"
                  >
                    <option value="free">🆓 Free (10 registros/mes)</option>
                    <option value="premium">⭐ Premium (Ilimitado)</option>
                    <option value="unlimited">💎 Unlimited (Ilimitado + Soporte)</option>
                  </select>
                </div>

                <div className="form-group checkbox-group">
                  <label>
                    <input
                      type="checkbox"
                      name="email_verified"
                      defaultChecked={editModal.user.email_verified}
                    />
                    Email verificado
                  </label>
                </div>
                
                <div className="form-group checkbox-group">
                  <label>
                    <input
                      type="checkbox"
                      name="follow_up_notifications"
                      defaultChecked={editModal.user.follow_up_notifications}
                    />
                    Notificaciones de seguimiento
                  </label>
                </div>
                
                <div className="form-group checkbox-group">
                  <label>
                    <input
                      type="checkbox"
                      name="follow_up_enabled"
                      defaultChecked={editModal.user.follow_up_enabled}
                    />
                    Seguimiento habilitado
                  </label>
                </div>
                
                <div className="modal-actions">
                  <button type="submit" className="btn btn-primary">
                    Guardar Cambios
                  </button>
                  <button
                    type="button"
                    onClick={() => setEditModal({ user: {} as User, isOpen: false })}
                    className="btn btn-cancel"
                  >
                    Cancelar
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {/* Modal de Confirmación */}
      {confirmModal.isOpen && (
        <div className="modal-overlay">
          <div className="modal confirm-modal">
            <div className="modal-header">
              <h3>{confirmModal.title}</h3>
            </div>
            <div className="modal-body">
              <p>{confirmModal.message}</p>
            </div>
            <div className="modal-actions">
              <button
                onClick={handleConfirmAction}
                className={`btn ${confirmModal.type === 'delete' ? 'btn-danger' : 'btn-primary'}`}
              >
                Confirmar
              </button>
              <button
                onClick={() => setConfirmModal({ isOpen: false, title: '', message: '', type: 'delete' })}
                className="btn btn-cancel"
              >
                Cancelar
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminUsers;